import { RINK } from '../types';
import type { PhysicsConfig } from './physics';

/**
 * Game modifiers patch the per-match PhysicsConfig at match start. Each
 * peer reconstructs the same config by ID, so only the modifier IDs
 * need to cross the wire (not the resolved config values). Modifiers
 * compose by sequential application — earlier wins on conflicts only
 * if a later one doesn't touch the same field.
 *
 * New modifier types in the future may also need onTick / onEvent hooks
 * (for things like magnetic goals, bullet time, periodic activations).
 * Add them to the interface when the first such modifier lands.
 */
export interface Modifier {
  id: string;
  name: string;
  description: string;
  apply(cfg: PhysicsConfig): void;
}

export const MODIFIERS: readonly Modifier[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Standard rules.',
    apply: () => { /* baseline — no changes */ },
  },
  {
    id: 'big-paddle',
    name: 'Big paddle',
    description: 'Paddles are 50% bigger. Easier to defend, harder to dodge.',
    apply: (cfg) => {
      // Scale BOTH axes so the paddle stays a circle. Previously only
      // paddleRadius grew, which made the paddle elongate in depth.
      cfg.paddleRadius = Math.round(cfg.paddleRadius * 1.5);
      cfg.paddleHalfHeight = Math.round(cfg.paddleHalfHeight * 1.5);
      // Re-derive paddle x-clamp bounds since they depend on radius.
      cfg.hostXMax = RINK.width / 2 - cfg.paddleRadius;
      cfg.guestXMin = RINK.width / 2 + cfg.paddleRadius;
    },
  },
  {
    id: 'pong',
    name: 'Pong',
    description: 'Slim wide paddle across the back wall — whole back wall is goal.',
    apply: (cfg) => {
      // Slim wide bar: paddleHalfHeight is the LATERAL half-extent (the
      // wide axis from the player's POV), paddleRadius is the depth
      // half-extent (thin slab). 100 wide × 16 thin covers ~20% of the
      // back wall — classic Pong proportion.
      cfg.paddleRadius = 8;
      cfg.paddleHalfHeight = 50;
      // Pin tight to the back wall — only a sliver of forward movement.
      const strip = cfg.paddleRadius * 2;
      cfg.hostXMax = cfg.paddleRadius + strip;
      cfg.guestXMin = RINK.width - cfg.paddleRadius - strip;
      // Entire back wall is goal mouth — no rim wall, no posts.
      cfg.goalHalfHeight = RINK.height / 2;
      // Shallow pocket — puck scores almost immediately on crossing
      // into the back area, matching Pong's instant-score feel.
      cfg.pocketDepth = 20;
    },
  },
];

export function applyModifiers(cfg: PhysicsConfig, ids: readonly string[]): void {
  for (const id of ids) {
    const mod = MODIFIERS.find((m) => m.id === id);
    if (mod) mod.apply(cfg);
  }
}

export function getModifier(id: string): Modifier | undefined {
  return MODIFIERS.find((m) => m.id === id);
}
